import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Table,
  Spinner,
  Alert,
  Container,
  Card,
  Button,
  Modal,
  Form,
  Badge,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import HusariaHeader from "views/pages/components/HusariaHeader";
import { Input } from "reactstrap";
const MusicOnHold = () => {
  const { theme } = useContext(ThemeContext);
  const isDark = theme === "dark";
  const audioRef = useRef(null);

  const visibleColumns = [
    { key: "filename", label: "Filename" },
    { key: "rate", label: "Bitrate (kbps)" },
    { key: "file_size", label: "Size" },
    { key: "created_date", label: "Uploaded" },
  ];

  const tableName = "v_music_on_hold";
  const title = "Music on Hold";
  const parentName = "HUCS";
  const rowKey = "filename";
  const [rows, setRows] = useState([]);
  const [groupedRows, setGroupedRows] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [rowToDelete, setRowToDelete] = useState(null);
  const [currentlyPlaying, setCurrentlyPlaying] = useState(null);
  const [uploadData, setUploadData] = useState({
    file: null,
    rate: "8",
    description: "",
  });
  const [uploading, setUploading] = useState(false);
  const navigate = useNavigate();
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const progress = duration ? (currentTime / duration) * 100 : 0;
  const fetchRows = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${serverHost}/get_all_music`);
      if (!response.ok) throw new Error(`Failed to fetch ${tableName}`);
      const data = await response.json();
      const results = data.results || [];
      const music = results.map((s) => ({
        ...s,
      }));
      console.log(music);
      setRows(music);

      // Group by bitrate
      const grouped = music.reduce((acc, row) => {
        const rate = row.rate || "Unknown";
        if (!acc[rate]) {
          acc[rate] = [];
        }
        acc[rate].push(row);
        return acc;
      }, {});

      setGroupedRows(grouped);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRows();
  }, []);

  const toggleDeleteModal = (row = null) => {
    setRowToDelete(row);
    setDeleteModal(!deleteModal);
  };

  const toggleUploadModal = () => {
    setUploadModal(!uploadModal);
    if (!uploadModal) {
      // Reset form when opening
      setUploadData({
        file: null,
        rate: "8000",
        description: "",
      });
    }
  };

  const handleDelete = async () => {
    if (!rowToDelete) return;
    try {
      const response = await fetch(
        `${serverHost}/delete_music_on_hold/${rowToDelete[rowKey]}`,
        {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
        }
      );
      if (!response.ok) throw new Error("Delete failed");
      await response.json();
      toggleDeleteModal();
      fetchRows();
    } catch (err) {
      alert(`Error deleting file: ${err.message}`);
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const validTypes = [
        "audio/mpeg",
        "audio/wav",
        "audio/mp3",
        "audio/x-wav",
      ];
      if (
        !validTypes.includes(file.type) &&
        !file.name.match(/\.(mp3|wav)$/i)
      ) {
        alert("Please upload a valid audio file (MP3 or WAV)");
        return;
      }
      setUploadData((prev) => ({ ...prev, file }));
    }
  };

  const handleUpload = async () => {
    if (!uploadData.file) {
      alert("Please select a file to upload");
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", uploadData.file);
      formData.append("rate", uploadData.rate);
      formData.append("description", uploadData.description);

      const response = await fetch(`${serverHost}/upload_music`, {
        method: "POST",
        body: formData,
      });

      if (!response.ok) throw new Error("Upload failed");
      await response.json();

      toggleUploadModal();
      fetchRows();
      alert("File uploaded successfully!");
    } catch (err) {
      alert(`Error uploading file: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  const handlePlayAudio = (row) => {
    if (currentlyPlaying === row[rowKey]) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
      setDuration(0);
      setCurrentlyPlaying(null);
    } else {
      audioRef.current.src = `${serverHost}/get_music/${row[rowKey]}:${row["rate"]}`;
      audioRef.current.play();
      setCurrentTime(0);
      setDuration(0);
      setCurrentlyPlaying(row[rowKey]);
    }
  };

  const handleAudioEnded = () => {
    setCurrentlyPlaying(null);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return "-";
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "-";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" variant={isDark ? "light" : "primary"} />
        <p className="mt-2" style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
          Loading {title?.toLowerCase() || "data"}...
        </p>
      </div>
    );

  if (error)
    return (
      <Alert variant="danger" className="mt-5 text-center">
        {error}
      </Alert>
    );

  return (
    <>
      <HusariaHeader name={title} parentName={parentName} padding="4.5rem" />
      <Container fluid className="mt--6">
        {/* Hidden audio element for playback */}
        <audio
          ref={audioRef}
          onEnded={handleAudioEnded}
          onTimeUpdate={() => {
            if (audioRef.current) {
              setCurrentTime(audioRef.current.currentTime);
            }
          }}
          onLoadedMetadata={() => {
            if (audioRef.current) {
              setDuration(audioRef.current.duration);
            }
          }}
        />
        {currentlyPlaying && (
          <Card
            className={`mb-3 ${isDark ? "bg-dark text-light" : ""}`}
            style={{ borderLeft: "4px solid #0d6efd" }}
          >
            <Card.Body className="py-2">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <strong>Now Playing:</strong>
                <span>{currentlyPlaying}</span>
              </div>

              <div
                style={{
                  height: "8px",
                  backgroundColor: isDark ? "#374151" : "#e5e7eb",
                  borderRadius: "4px",
                  overflow: "hidden",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  const percent = (e.clientX - rect.left) / rect.width;
                  audioRef.current.currentTime = percent * duration;
                }}
              >
                <div
                  style={{
                    width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                    height: "100%",
                    backgroundColor: "#0d6efd",
                    transition: "width 0.1s linear",
                  }}
                />
              </div>

              <div
                className="d-flex justify-content-between mt-1"
                style={{ fontSize: "0.8rem" }}
              >
                <span>{formatDuration(currentTime)}</span>
                <span>{formatDuration(duration)}</span>
              </div>
            </Card.Body>
          </Card>
        )}
        {Object.keys(groupedRows)
          .sort((a, b) => parseInt(a) - parseInt(b))
          .map((rate) => (
            <Card
              key={rate}
              className={`shadow-sm mb-4 ${isDark ? "bg-dark text-light" : ""}`}
            >
              <Card.Header
                style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}
                className="d-flex justify-content-between align-items-center"
              >
                <div className="d-flex align-items-center gap-3">
                  <h4 className="mb-0">BITRATE: {rate} kbps</h4>
                  <Badge bg="info">{groupedRows[rate].length} files</Badge>
                </div>

                <div className="d-flex gap-2">
                  <Button variant="info" size="sm" onClick={fetchRows}>
                    Refresh
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={toggleUploadModal}
                  >
                    + Upload Music
                  </Button>
                </div>
              </Card.Header>

              <Card.Body
                style={{
                  padding: 0,
                  backgroundColor: isDark ? "#1f2937" : "#fff",
                }}
              >
                <div style={{ overflowX: "auto" }}>
                  <Table
                    striped
                    hover
                    responsive
                    bordered
                    className={`mb-0`}
                    style={{
                      minWidth: "1000px",
                      backgroundColor: isDark ? "#1f2937" : "#fff",
                      color: isDark ? "#e2e8f0" : "#212529",
                    }}
                  >
                    <thead
                      style={{
                        backgroundColor: isDark ? "#1f2937" : "#fff",
                        fontWeight: "bold",
                        color: isDark ? "#e2e8f0" : "#212529",
                      }}
                    >
                      <tr>
                        {visibleColumns.map((col) => (
                          <th
                            key={col.key || col.label}
                            className="text-center"
                          >
                            {col.label}
                          </th>
                        ))}
                        <th className="text-center">Preview</th>
                        <th className="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupedRows[rate].map((row) => {
                        const idValue = row[rowKey];
                        const isPlaying = currentlyPlaying === idValue;

                        return (
                          <tr
                            key={idValue}
                            style={{ color: isDark ? "#e2e8f0" : "#212529" }}
                          >
                            {visibleColumns.map((col, index) => {
                              let value = row[col.key] ?? "-";
                              // Format special columns
                              if (col.key === "file_size") {
                                value = formatFileSize(value);
                              } else if (col.key === "duration") {
                                value = formatDuration(value);
                              } else if (
                                col.key === "created_date" &&
                                value !== "-"
                              ) {
                                value = new Date(value).toLocaleDateString();
                              }

                              const isLink = index === 0;

                              return (
                                <td
                                  key={`${idValue}-${col.key}`}
                                  className="text-center"
                                >
                                  {isLink ? (
                                    <span
                                      style={{
                                        color: isDark ? "#60a5fa" : "#0d6efd",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {value}
                                    </span>
                                  ) : (
                                    <span style={{ fontWeight: 400 }}>
                                      {value}
                                    </span>
                                  )}
                                </td>
                              );
                            })}
                            <td className="text-center">
                              <Button
                                variant={isPlaying ? "warning" : "success"}
                                size="sm"
                                onClick={() => handlePlayAudio(row)}
                              >
                                {isPlaying ? "⏸ Stop" : "▶ Play"}
                              </Button>
                            </td>
                            <td className="text-center">
                              <Button
                                variant="danger"
                                size="sm"
                                onClick={() => toggleDeleteModal(row)}
                              >
                                Delete
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </Table>
                </div>
              </Card.Body>
            </Card>
          ))}

        {Object.keys(groupedRows).length === 0 && !loading && (
          <Card>
          <Card.Header
                style={{
                  backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                  color: isDark ? "#e2e8f0" : "#111",
                }}
                className="d-flex justify-content-between align-items-center"
              >
                <div className="d-flex gap-2">
                  <Button variant="info" size="sm" onClick={fetchRows}>
                    Refresh
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={toggleUploadModal}
                  >
                    + Upload Music
                  </Button>
                </div>
              </Card.Header>
          <Alert variant="info" className="text-center">
            No music files found. Click "Upload Music" to add some.
          </Alert>
          </Card>
        )}
      </Container>

      {/* Upload Modal */}
      <Modal show={uploadModal} onHide={toggleUploadModal} size="lg">
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>Upload Music on Hold</Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: isDark ? "#111827" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Audio File (MP3 or WAV)</Form.Label>
              <Form.Control
                type="file"
                accept="audio/mpeg,audio/wav,audio/mp3,.mp3,.wav"
                onChange={handleFileChange}
                style={{
                  backgroundColor: isDark ? "#374151" : "#fff",
                  color: isDark ? "#e2e8f0" : "#212529",
                  borderColor: isDark ? "#4b5563" : "#ced4da",
                }}
              />
              {uploadData.file && (
                <Form.Text style={{ color: isDark ? "#9ca3af" : "#6c757d" }}>
                  Selected: {uploadData.file.name} (
                  {(uploadData.file.size / 1024 / 1024).toFixed(2)} MB)
                </Form.Text>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Bitrate (kbps)</Form.Label>
              <Input
                type="select"
                value={uploadData.rate}
                onChange={(e) =>
                  setUploadData((prev) => ({ ...prev, rate: e.target.value }))
                }
                style={{
                  backgroundColor: isDark ? "#374151" : "#fff",
                  color: isDark ? "#e2e8f0" : "#212529",
                  borderColor: isDark ? "#4b5563" : "#ced4da",
                }}
              >
                <option value="8">8 kbps</option>
                <option value="16">16 kbps</option>
                <option value="32">32 kbps</option>
                <option value="48">48 kbps</option>
              </Input>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Description (Optional)</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={uploadData.description}
                onChange={(e) =>
                  setUploadData((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Enter a description for this music file..."
                style={{
                  backgroundColor: isDark ? "#374151" : "#fff",
                  color: isDark ? "#e2e8f0" : "#212529",
                  borderColor: isDark ? "#4b5563" : "#ced4da",
                }}
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: isDark ? "#1f2937" : "#f8f9fa",
          }}
        >
          <Button
            variant="light"
            onClick={toggleUploadModal}
            disabled={uploading}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpload}
            disabled={uploading || !uploadData.file}
          >
            {uploading ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  className="me-2"
                />
                Uploading...
              </>
            ) : (
              "Upload"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={deleteModal} onHide={toggleDeleteModal}>
        <Modal.Header
          closeButton
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
          }}
        >
          <Modal.Title style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
            Delete Music File
          </Modal.Title>
        </Modal.Header>
        <Modal.Body
          style={{
            backgroundColor: isDark ? "#1f2937" : "#fff",
            color: isDark ? "#e2e8f0" : "#212529",
          }}
        >
          Are you sure you want to delete this <strong>{rowToDelete?.filename}</strong>? This action
          cannot be undone.
        </Modal.Body>
        <Modal.Footer
          style={{
            backgroundColor: isDark ? "#111827" : "#f8f9fa",
          }}
        >
          <Button variant="light" onClick={toggleDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDelete}>
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MusicOnHold;
